// Culture-styled city centres: village / town / capital for each player type
// (docs/02-systems/building-styles.md). Pure functions of the style table:
// the same (style, tier) always yields the same voxels, no randomness.
//
// Every centre is built from the same vocabulary — team plinth, base course,
// wall shell, roof, banner, landmark — so a village reads as a small piece of
// the capital. Roof and banner shapes come from lookup tables keyed by the
// style's `roof` / `banner`; nothing here branches on a building kind.
//
// Team colour: the plinth slab and the banner cloth are `team` voxels (greys
// the renderer recolours to the owner), so two players of one culture differ
// by that always-visible accent, and the culture palette keeps its colours
// (a prefab with team voxels gets a 12% tint instead of the legacy 35%).

import {
  BUILDING_STYLES,
  type BannerShape,
  type BuildingStyleId,
  type RoofShape,
  type StylePalette,
} from './styles.js';
import type { BlockVoxel, StructurePrefab } from './types.js';

/** City-centre tiers: 1 village / outpost, 2 town, 3 capital. */
export type CentreTier = 1 | 2 | 3;
export const CENTRE_TIERS: readonly CentreTier[] = [1, 2, 3];

/** Styles that get their own centres (everything but `neutral`). */
export type CentreStyleId = Exclude<BuildingStyleId, 'neutral'>;

const TEAM_LIT = '#d2d2d2';
const TEAM_FOLD = '#a8a8a8';

type V = BlockVoxel;

function box(
  sx: number,
  sy: number,
  sz: number,
  w: number,
  h: number,
  d: number,
  color: string,
  team = false,
): V[] {
  const out: V[] = [];
  for (let x = sx; x < sx + w; x++)
    for (let y = sy; y < sy + h; y++)
      for (let z = sz; z < sz + d; z++)
        out.push(team ? { x, y, z, color, team: true } : { x, y, z, color });
  return out;
}

/** Hollow rectangular ring, one layer thick per y, centred on the origin: |x|,|z| <= half. */
function shell(
  half: number,
  y0: number,
  h: number,
  color: (x: number, y: number, z: number) => string,
): V[] {
  const out: V[] = [];
  for (let y = y0; y < y0 + h; y++)
    for (let x = -half; x <= half; x++)
      for (let z = -half; z <= half; z++)
        if (Math.abs(x) === half || Math.abs(z) === half)
          out.push({ x, y, z, color: color(x, y, z) });
  return out;
}

/** Solid square slab, |x|,|z| <= half. */
function slab(half: number, y: number, color: string): V[] {
  return box(-half, y, -half, 2 * half + 1, 1, 2 * half + 1, color);
}

function disc(r: number, y: number, color: string): V[] {
  const out: V[] = [];
  const lim = r * r + r * 0.5;
  for (let x = -Math.ceil(r); x <= Math.ceil(r); x++)
    for (let z = -Math.ceil(r); z <= Math.ceil(r); z++)
      if (x * x + z * z <= lim) out.push({ x, y, z, color });
  return out;
}

/** Later voxels win at a shared position (same rule as the geometry cache). */
function dedupe(blocks: readonly V[]): V[] {
  const m = new Map<string, V>();
  for (const b of blocks) m.set(`${b.x},${b.y},${b.z}`, b);
  return [...m.values()].sort((a, b) => a.y - b.y || a.x - b.x || a.z - b.z);
}

interface TierSpec {
  /** Outer half-extent: plinth, roof overhang and columns stay within it. */
  readonly B: number;
  /** Wall shell half-extent. */
  readonly wallHalf: number;
  readonly wallH: number;
}

const TIER_SPECS: Readonly<Record<CentreTier, TierSpec>> = {
  1: { B: 3, wallHalf: 2, wallH: 3 },
  2: { B: 4, wallHalf: 3, wallH: 3 },
  3: { B: 5, wallHalf: 4, wallH: 4 },
};

/** First wall layer: plinth is y=0, base course y=1. */
const WALL_Y = 2;

interface Ctx {
  readonly style: CentreStyleId;
  readonly p: StylePalette;
  readonly tier: CentreTier;
  readonly spec: TierSpec;
}

// --- Foundation: team plinth border + base course --------------------------

function foundation(c: Ctx): V[] {
  const { B } = c.spec;
  return [
    ...box(-B, 0, -B, 2 * B + 1, 1, 2 * B + 1, TEAM_LIT, true),
    // Fold line so the plinth reads as paving, not a flat slab.
    ...box(-B, 0, -B, 2 * B + 1, 1, 1, TEAM_FOLD, true),
    ...box(-B, 0, B, 2 * B + 1, 1, 1, TEAM_FOLD, true),
    ...slab(B - 1, 1, c.p.base),
  ];
}

// --- Walls ------------------------------------------------------------------

type WallRule = (c: Ctx) => (x: number, y: number, z: number) => string;

/** Wall colouring per roof family: plaster + cornice, plank stripes, plaster + vermilion posts. */
const WALL_RULES: Readonly<Record<RoofShape, WallRule>> = {
  'hip-low': (c) => (_x, y) => (y === WALL_Y + c.spec.wallH - 1 ? c.p.trim : c.p.wall),
  'gable-steep': (c) => (x, _y, z) => ((x + z) % 2 === 0 ? c.p.wall : c.p.wallAlt),
  'swept-tiers': (c) => (x, y, z) =>
    (Math.abs(x) === c.spec.wallHalf && Math.abs(z) === c.spec.wallHalf) ||
    y === WALL_Y + c.spec.wallH - 1
      ? c.p.trim
      : c.p.wall,
  shared: (c) => () => c.p.wall,
};

function walls(
  c: Ctx,
  roof: RoofShape,
  half = c.spec.wallHalf,
  y0 = WALL_Y,
  h = c.spec.wallH,
): V[] {
  const out = shell(half, y0, h, WALL_RULES[roof](c));
  if (y0 !== WALL_Y) return out;
  // Door and windows on the front (+z) face.
  out.push(
    { x: 0, y: y0, z: half, color: c.p.door },
    { x: 0, y: y0 + 1, z: half, color: c.p.door },
  );
  for (const wx of half >= 3 ? [-2, 2] : [-1, 1]) {
    out.push({ x: wx, y: y0 + 1, z: half, color: c.p.glass });
  }
  return out;
}

// --- Roofs: return voxels plus the apex (x=0,z=0) y of the topmost layer ----

interface Roofed {
  readonly blocks: V[];
  readonly topY: number;
}

type RoofBuilder = (c: Ctx) => Roofed;

const ROOFS: Readonly<Record<RoofShape, RoofBuilder>> = {
  // Low hip: three shrinking slabs, terracotta with a lighter ridge course.
  'hip-low': (c) => {
    const y0 = WALL_Y + c.spec.wallH;
    const blocks: V[] = [...shell(c.spec.B, y0, 1, () => c.p.trim)];
    let y = y0;
    for (let ext = c.spec.B; ext >= c.spec.B - 2; ext--) {
      blocks.push(...slab(ext, y, y === y0 + 2 ? c.p.roofAlt : c.p.roof));
      y++;
    }
    return { blocks, topY: y - 1 };
  },
  // Steep gable: ridge along x, one layer narrower per step, plank gable ends, crossed finials.
  'gable-steep': (c) => {
    const y0 = WALL_Y + c.spec.wallH;
    const xe = c.spec.wallHalf + 1;
    const blocks: V[] = [];
    let y = y0;
    for (let zh = c.spec.wallHalf + 1; zh >= 0; zh--, y++) {
      for (let x = -xe; x <= xe; x++)
        for (let z = -zh; z <= zh; z++)
          blocks.push({
            x,
            y,
            z,
            color: Math.abs(x) === xe ? c.p.wallAlt : (z + y) % 2 === 0 ? c.p.roof : c.p.roofAlt,
          });
    }
    const ridge = y - 1;
    for (const sx of [-xe, xe]) {
      blocks.push(
        { x: sx, y: ridge + 1, z: 0, color: c.p.accent },
        { x: sx, y: ridge + 2, z: 0, color: c.p.accent },
        { x: sx, y: ridge + 1, z: -1, color: c.p.accent },
        { x: sx, y: ridge + 1, z: 1, color: c.p.accent },
      );
    }
    return { blocks, topY: ridge };
  },
  // Pagoda tiers: built by `pagoda` below, which owns walls and roofs together.
  'swept-tiers': (c) => pagoda(c),
  shared: (c) => ({
    blocks: slab(c.spec.B, WALL_Y + c.spec.wallH, c.p.roof),
    topY: WALL_Y + c.spec.wallH,
  }),
};

/** Eastern: stacked tiers of (walls, swept eave, step), corner tips curling up. */
function pagoda(c: Ctx): Roofed {
  const { tier } = c;
  const tiers: ReadonlyArray<{ wall: number; eave: number; wallH: number }> =
    tier === 1
      ? [{ wall: 2, eave: 3, wallH: 3 }]
      : tier === 2
        ? [
            { wall: 3, eave: 4, wallH: 2 },
            { wall: 1, eave: 2, wallH: 2 },
          ]
        : [
            { wall: 4, eave: 5, wallH: 2 },
            { wall: 2, eave: 3, wallH: 1 },
            { wall: 1, eave: 2, wallH: 1 },
          ];
  const blocks: V[] = [];
  let y = WALL_Y;
  for (const t of tiers) {
    // Plaster walls with vermilion corner posts and a beam band on the top layer.
    blocks.push(
      ...shell(t.wall, y, t.wallH, (x, _y, z) =>
        Math.abs(x) === t.wall && Math.abs(z) === t.wall ? c.p.trim : c.p.wall,
      ),
      ...shell(t.wall, y + t.wallH - 1, 1, () => c.p.trim),
    );
    if (y === WALL_Y) {
      blocks.push(
        { x: 0, y, z: t.wall, color: c.p.door },
        { x: 0, y: y + 1, z: t.wall, color: c.p.door },
      );
    }
    y += t.wallH;
    // Eave slab with a vermilion edge board, so the swept silhouette reads from above.
    blocks.push(...slab(t.eave, y, c.p.roof), ...shell(t.eave, y, 1, () => c.p.trim));
    // Upswept corners: the eave tips curl above the roof line.
    for (const sx of [-t.eave, t.eave])
      for (const sz of [-t.eave, t.eave])
        blocks.push({ x: sx, y: y + 1, z: sz, color: c.p.roofAlt });
    y++;
    blocks.push(...slab(Math.max(0, t.eave - 1), y, c.p.roofAlt));
    y++;
  }
  blocks.push({ x: 0, y, z: 0, color: c.p.accent });
  return { blocks, topY: y };
}

// --- Landmarks: per-style extras that grow with tier ------------------------

type LandmarkBuilder = (c: Ctx, roof: Roofed) => V[];

const LANDMARKS: Readonly<Record<CentreStyleId, LandmarkBuilder>> = {
  // Columns on the front face; a dome from tier 2; pediment + bigger dome at tier 3.
  imperial: (c, roof) => {
    const { B } = c.spec;
    const out: V[] = [];
    const cols = c.tier === 1 ? [-2, 2] : c.tier === 2 ? [-3, -1, 1, 3] : [-4, -2, 0, 2, 4];
    for (const x of cols) out.push(...box(x, 1, B, 1, c.spec.wallH + 1, 1, c.p.wall));
    if (c.tier === 3) {
      // Pediment over the colonnade.
      for (let i = 0; i < 5; i++)
        out.push(
          ...box(
            -(4 - i),
            roof.topY - 2 + i,
            B,
            2 * (4 - i) + 1,
            1,
            1,
            i === 0 ? c.p.trim : c.p.wallAlt,
          ),
        );
    }
    if (c.tier === 3) {
      // Showpiece skyline: two slim campanile towers behind the dome, gold-capped.
      for (const sx of [-1, 1]) {
        const x0 = sx < 0 ? -B : B - 1;
        out.push(...box(x0, 1, -B, 2, 9, 2, c.p.wall), ...box(x0, 10, -B, 2, 1, 2, c.p.trim));
        out.push(...box(x0, 11, -B, 2, 1, 2, c.p.roof), { x: x0, y: 12, z: -B, color: c.p.accent });
      }
    }
    if (c.tier >= 2) {
      const r = c.tier === 3 ? 3 : 2;
      let y = roof.topY + 1;
      for (let rr = r; rr >= 1; rr--, y++) out.push(...disc(rr, y, rr === r ? c.p.trim : c.p.wall));
      out.push({ x: 0, y, z: 0, color: c.p.accent });
    }
    return out;
  },
  // Stacked porch finials grow into a stave tower and a rune stone.
  nordic: (c) => {
    const out: V[] = [];
    if (c.tier >= 2) {
      // Row of round shields along the front eaves.
      for (let x = -c.spec.wallHalf + 1; x <= c.spec.wallHalf - 1; x += 2)
        out.push({ x, y: WALL_Y + c.spec.wallH - 1, z: c.spec.wallHalf + 1, color: c.p.trim });
    }
    if (c.tier === 3) {
      // Stave tower: three narrowing storeys, each with its own little roof.
      const cx = -3;
      const cz = -3;
      let y = WALL_Y;
      for (let i = 0; i < 3; i++) {
        const half = i === 2 ? 0 : 1;
        for (const v of shell(half, y, 2, () => c.p.wall))
          out.push({ ...v, x: v.x + cx, z: v.z + cz });
        y += 2;
        for (const v of slab(half + 1, y, c.p.roof)) out.push({ ...v, x: v.x + cx, z: v.z + cz });
        y++;
      }
      out.push(
        { x: cx, y, z: cz, color: c.p.accent },
        { x: cx, y: y + 1, z: cz, color: c.p.accent },
      );
      // Second, shorter stave tower.
      for (let i = 0; i < 2; i++) {
        const cx2 = 3;
        const cz2 = -3;
        const y2 = WALL_Y + i * 2;
        for (const v of shell(i === 1 ? 0 : 1, y2, 2, () => c.p.wall))
          out.push({ ...v, x: v.x + cx2, z: v.z + cz2 });
        for (const v of slab((i === 1 ? 0 : 1) + 1, y2 + 2, c.p.roof))
          out.push({ ...v, x: v.x + cx2, z: v.z + cz2 });
      }
      // Rune stone beside the entrance.
      out.push(...box(4, 1, 5, 1, 3, 1, c.p.base), { x: 4, y: 3, z: 5, color: c.p.accent });
    }
    return out;
  },
  // Lantern posts at the gate; a torii from tier 3.
  eastern: (c, roof) => {
    const { B } = c.spec;
    const out: V[] = [];
    for (const x of [-B + 1, B - 1]) {
      out.push(...box(x, 1, B, 1, 2, 1, c.p.trim), { x, y: 3, z: B, color: c.p.accent });
    }
    if (c.tier === 3) {
      out.push(...box(-2, 1, B, 1, 4, 1, c.p.trim), ...box(2, 1, B, 1, 4, 1, c.p.trim));
      out.push(...box(-3, 5, B, 7, 1, 1, c.p.trim), ...box(-2, 4, B, 5, 1, 1, c.p.door));
      // Golden spire on the pagoda and paired stone lanterns flanking the gate.
      for (let dy = 1; dy <= 2; dy++)
        out.push({ x: 0, y: roof.topY + dy, z: 0, color: c.p.accent });
      for (const x of [-4, 4])
        out.push(...box(x, 1, B - 1, 1, 2, 1, c.p.base), { x, y: 3, z: B - 1, color: c.p.roof });
    }
    return out;
  },
};

// --- Banners: team-coloured cloth on a pole ---------------------------------

type BannerBuilder = (x: number, z: number, y0: number, h: number, dir: 1 | -1, c: Ctx) => V[];

const pole = (x: number, z: number, y0: number, h: number, color: string): V[] =>
  box(x, y0, z, 1, h, 1, color);

const BANNERS: Readonly<Record<BannerShape, BannerBuilder>> = {
  // Hanging cloth under a crossbar, gold fringe, gold finial.
  vexillum: (x, z, y0, h, dir, c) => [
    ...pole(x, z, y0, h, c.p.trim),
    { x, y: y0 + h, z, color: c.p.accent },
    ...box(dir > 0 ? x + 1 : x - 2, y0 + h - 3, z, 2, 3, 1, TEAM_LIT, true),
    ...box(dir > 0 ? x + 1 : x - 2, y0 + h - 4, z, 2, 1, 1, c.p.accent),
  ],
  // Tapering pennant streaming from a tall mast.
  'pennant-mast': (x, z, y0, h, dir, c) => [
    ...pole(x, z, y0, h, c.p.door),
    { x, y: y0 + h, z, color: c.p.accent },
    ...box(dir > 0 ? x + 1 : x - 3, y0 + h - 1, z, 3, 1, 1, TEAM_LIT, true),
    ...box(dir > 0 ? x + 1 : x - 2, y0 + h - 2, z, 2, 1, 1, TEAM_FOLD, true),
    ...box(dir > 0 ? x + 1 : x - 1, y0 + h - 3, z, 1, 1, 1, TEAM_FOLD, true),
  ],
  // Tall narrow nobori flag riding the pole.
  nobori: (x, z, y0, h, dir, c) => [
    ...pole(x, z, y0, h, c.p.door),
    ...box(x, y0 + h, z, 1, 1, 1, c.p.accent),
    ...box(dir > 0 ? x + 1 : x - 1, y0 + h - 5, z, 1, 5, 1, TEAM_LIT, true),
    ...box(dir > 0 ? x + 1 : x - 1, y0 + h - 6, z, 1, 1, 1, c.p.trim),
  ],
  shared: (x, z, y0, h, dir) => [
    ...pole(x, z, y0, h, '#654321'),
    ...box(dir > 0 ? x + 1 : x - 2, y0 + h - 2, z, 2, 2, 1, TEAM_LIT, true),
  ],
};

/** Banner anchors per tier: roof apex for the village, front corners above that for larger tiers. */
function banners(c: Ctx, roof: Roofed): V[] {
  const make = BANNERS[BUILDING_STYLES[c.style].banner];
  const { B } = c.spec;
  if (c.tier === 1) return make(0, 0, roof.topY + 1, 3, 1, c);
  const y0 = WALL_Y + c.spec.wallH + 1;
  const h = c.tier === 2 ? 4 : 6;
  return [...make(-B, B, y0, h, 1, c), ...make(B, B, y0, h, -1, c)];
}

// --- Assembly ---------------------------------------------------------------

const STYLE_NAMES: Readonly<Record<CentreStyleId, readonly [string, string, string]>> = {
  imperial: ['Imperial Villa', 'Imperial Forum', 'Imperial Palace'],
  nordic: ['Nordic Longhouse', 'Nordic Hall', 'Nordic Great Hall'],
  eastern: ['Eastern Hall', 'Eastern Tower', 'Eastern Pagoda'],
};

export function buildCentre(style: CentreStyleId, tier: CentreTier): StructurePrefab {
  const def = BUILDING_STYLES[style];
  const c: Ctx = { style, p: def.palette, tier, spec: TIER_SPECS[tier] };
  const blocks: V[] = [...foundation(c)];
  const roof = ROOFS[def.roof](c);
  // The pagoda owns its walls; every other roof sits on the common wall shell.
  if (def.roof !== 'swept-tiers') blocks.push(...walls(c, def.roof));
  blocks.push(...roof.blocks, ...LANDMARKS[style](c, roof), ...banners(c, roof));
  return { name: STYLE_NAMES[style][tier - 1] as string, blocks: dedupe(blocks) };
}

/** Prefab id of a style variant of `baseId`: `castle@imperial`. */
export function styledId(baseId: string, style: BuildingStyleId): string {
  return style === 'neutral' ? baseId : `${baseId}@${style}`;
}

/** The base prefab each tier replaces (the existing shared city centres). */
export const CENTRE_BASE_PREFAB: Readonly<Record<CentreTier, string>> = {
  1: 'house',
  2: 'tavern',
  3: 'castle',
};
